/**
 * Authentication Hook - Supabase Integration
 * Provides user authentication state and role information
 */

import { createContext, createElement, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@services/supabase'
import { API_BASE, isRemoteApiFromLocalhost } from '@utils/apiConfig'
import { markPasswordRecovery, detectRecoveryFromUrl } from '@utils/passwordRecovery'

export interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  error: Error | null
  mustChangePassword: boolean
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>
  isAuthenticated: boolean
}

interface UserRole {
  role: 'teacher' | 'student' | 'parent' | 'senco' | 'senior_staff' | 'admin' | 'sysadmin' | null
  schoolId: string | null
}

const initialAuthState: AuthState = {
  user: null,
  session: null,
  loading: true,
  error: null,
  mustChangePassword: false,
}

const signedOutAuthState: AuthState = {
  user: null,
  session: null,
  loading: false,
  error: null,
  mustChangePassword: false,
}

const AuthContext = createContext<AuthContextValue | null>(null)

const isInvalidRefreshError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String((error as any)?.message ?? '')
  return message.includes('Refresh Token') || message.includes('Invalid')
}

const mergeAuthUserWithProfile = (authUser: User, fullUserProfile: any): User => {
  if (!fullUserProfile) return authUser

  return {
    ...authUser,
    ...fullUserProfile,
    aud: authUser.aud,
    created_at: authUser.created_at,
    updated_at: authUser.updated_at,
  } as User
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authState, setAuthState] = useState<AuthState>({
    ...initialAuthState,
  })
  const sessionRequestIdRef = useRef(0)

  const applySession = useCallback(async (session: Session | null) => {
    const requestId = ++sessionRequestIdRef.current
    const nextUser = session?.user ?? null

    if (!nextUser?.id) {
      setAuthState(signedOutAuthState)
      return
    }

    try {
      const { data: fullUserProfile } = await supabase
        .from('users')
        .select('*')
        .eq('id', nextUser.id)
        .maybeSingle()

      if (sessionRequestIdRef.current !== requestId) return

      setAuthState({
        user: mergeAuthUserWithProfile(nextUser, fullUserProfile),
        session,
        loading: false,
        error: null,
        mustChangePassword: !!fullUserProfile?.must_change_password,
      })
    } catch {
      if (sessionRequestIdRef.current !== requestId) return

      setAuthState({
        user: nextUser,
        session,
        loading: false,
        error: null,
        mustChangePassword: false,
      })
    }
  }, [])

  useEffect(() => {
    detectRecoveryFromUrl()

    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        // If it's a refresh token error, clear the session and continue
        if (error.message?.includes('Refresh Token') || error.message?.includes('Invalid')) {
          console.warn('⚠️ Invalid refresh token, clearing session...')
          supabase.auth.signOut({ scope: 'local' }).catch(() => {
            // Ignore sign out errors
          })
          setAuthState(signedOutAuthState)
        } else {
          setAuthState(prev => ({ ...prev, error, loading: false }))
        }
      } else {
        applySession(session)
      }
    }).catch((error) => {
      // Handle any unexpected errors
      if (isInvalidRefreshError(error)) {
        console.warn('⚠️ Invalid refresh token, clearing session...')
        supabase.auth.signOut({ scope: 'local' }).catch(() => {})
        setAuthState(signedOutAuthState)
      } else {
        setAuthState(prev => ({ ...prev, error, loading: false }))
      }
    })

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Dashboard "Send password recovery" and in-app forgot-password both land here.
      // Route the user to the set-new-password screen (no current-password prompt).
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecovery()
      }
      applySession(session)
    })

    return () => subscription.unsubscribe()
  }, [applySession])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      setAuthState(prev => ({ ...prev, error }))
    }
  }, [])

  const value: AuthContextValue = {
    user: authState.user,
    session: authState.session,
    loading: authState.loading,
    error: authState.error,
    mustChangePassword: authState.mustChangePassword,
    signOut,
    isAuthenticated: !!authState.user
  }

  return createElement(AuthContext.Provider, { value }, children)
}

export const useAuth = () => {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return value
}

const API_BASE_URL = API_BASE

export const useUserRole = (userId: string | undefined): UserRole & { loading: boolean } => {
  const { session } = useAuth()
  const [role, setRole] = useState<UserRole['role']>(null)
  const [schoolId, setSchoolId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    let cancelled = false
    const controller = new AbortController()
    const timeoutMs = 15000
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const fetchUserRole = async () => {
      try {
        const token = session?.access_token

        if (token && !cancelled && !isRemoteApiFromLocalhost()) {
          timeoutId = setTimeout(() => controller.abort(), timeoutMs)
          const res = await fetch(`${API_BASE_URL}/api/account/me`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
            signal: controller.signal,
          })
          if (timeoutId != null) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
          if (cancelled) return
          if (res.ok) {
            const json = await res.json()
            const payload = json?.data ?? json
            if (payload?.id === userId || payload?.role != null) {
              setRole((payload.role ?? null) as UserRole['role'])
              setSchoolId(payload.school_id ?? null)
              setLoading(false)
              return
            }
          }
        }

        if (cancelled) return
        const { data, error } = await supabase
          .from('users')
          .select('role, school_id')
          .eq('id', userId)
          .single()

        if (error) throw error
        if (cancelled) return

        setRole(data.role as UserRole['role'])
        setSchoolId(data.school_id)
      } catch (error: any) {
        if (cancelled) return
        if (error?.name === 'AbortError') {
          try {
            const { data, error: supabaseError } = await supabase
              .from('users')
              .select('role, school_id')
              .eq('id', userId)
              .single()
            if (!supabaseError && data) {
              setRole(data.role as UserRole['role'])
              setSchoolId(data.school_id)
              setLoading(false)
              return
            }
          } catch {
            // fall through
          }
          setRole(null)
          setSchoolId(null)
          setLoading(false)
          return
        }
        console.error('Error fetching user role:', error?.message || error, error)
        setRole(null)
        setSchoolId(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchUserRole()

    return () => {
      cancelled = true
      if (timeoutId != null) clearTimeout(timeoutId)
    }
  }, [userId, session?.access_token])

  return { role, schoolId, loading }
}

